import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import PricingSection from "@/components/PricingSection";
import { Bot, Mic, UserCircle, Video, Brain, Sparkles, MessageSquare, Phone, Mail, Calendar, Database, Zap } from "lucide-react";

const AIAutomation = () => {
  const services = [
    {
      icon: Bot,
      title: "AI Chatbots",
      description: "Intelligent conversational AI that handles customer inquiries 24/7, qualifies leads, and provides instant support across your website and social media platforms.",
      features: ["Natural language processing", "Multi-channel deployment", "CRM integration", "Custom training on your data"]
    },
    {
      icon: Mic,
      title: "Voice AI Agents",
      description: "Advanced voice automation for phone systems that can handle calls, schedule appointments, answer questions, and transfer to human agents when needed.",
      features: ["Natural voice interactions", "Call routing & transfer", "Appointment scheduling", "Multi-language support"]
    },
    {
      icon: UserCircle,
      title: "AI Avatar Clones",
      description: "Create realistic AI-powered digital avatars that represent your brand, deliver presentations, and engage with customers in a personalized way.",
      features: ["Custom avatar creation", "Voice cloning", "Video generation", "Brand personality matching"]
    },
    {
      icon: Video,
      title: "AI Video Creation",
      description: "Automated video production using Sora-2 AI technology. Create professional marketing videos, product demos, and social media content at scale.",
      features: ["Script-to-video generation", "Professional editing", "Brand template library", "Social media optimization"]
    },
    {
      icon: MessageSquare,
      title: "AI Social Media Management",
      description: "Automated content creation, posting, and engagement across all major social platforms. AI-powered analytics optimize your social strategy in real-time.",
      features: ["Content generation", "Auto-scheduling", "Engagement automation", "Performance analytics"]
    },
    {
      icon: Mail,
      title: "Email AI Automation",
      description: "Smart email marketing that writes, personalizes, and sends campaigns based on customer behavior and AI-driven insights.",
      features: ["Personalized campaigns", "A/B testing", "Behavioral triggers", "Performance tracking"]
    },
    {
      icon: Calendar,
      title: "Smart Scheduling",
      description: "AI-powered appointment booking that integrates with your calendar, sends reminders, and optimizes scheduling based on availability and preferences.",
      features: ["Automated booking", "Calendar sync", "SMS/Email reminders", "Timezone management"]
    },
    {
      icon: Database,
      title: "CRM Automation",
      description: "Intelligent customer relationship management that automatically tracks interactions, segments audiences, and triggers personalized follow-ups.",
      features: ["Contact management", "Lead scoring", "Pipeline automation", "Integration with major CRMs"]
    },
    {
      icon: Brain,
      title: "AI Marketing Automation",
      description: "End-to-end marketing automation that creates campaigns, optimizes ad spend, nurtures leads, and converts prospects into customers.",
      features: ["Campaign creation", "Ad optimization", "Lead nurturing", "ROI tracking"]
    },
    {
      icon: Zap,
      title: "Custom AI Integrations",
      description: "Connect AI to your existing tools and workflows. Custom integrations with CRMs, payment processors, scheduling systems, and business databases.",
      features: ["API integrations", "Workflow automation", "Data synchronization", "Custom development"]
    },
    {
      icon: Phone,
      title: "AI Call Center",
      description: "Complete call center automation with AI agents that can handle inbound and outbound calls, qualify leads, and provide customer support at scale.",
      features: ["Inbound/outbound calls", "Lead qualification", "Support automation", "Call analytics"]
    },
    {
      icon: Sparkles,
      title: "AI Content Studio",
      description: "Comprehensive content creation suite that generates blog posts, social media content, ad copy, and marketing materials using advanced AI.",
      features: ["Blog writing", "Ad copywriting", "Social content", "SEO optimization"]
    }
  ];

  const industries = [
    "Restaurants & Hospitality",
    "Healthcare & Medical",
    "Retail & eCommerce",
    "Real Estate",
    "Professional Services",
    "Education & Training",
    "Financial Services",
    "Manufacturing & Warehouses"
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="AI Automation Services - Neil AI Global Solutions"
        description="Comprehensive AI automation services including chatbots, voice agents, avatar clones, video creation, and marketing automation to transform your business operations."
        keywords="AI automation, chatbots, voice AI, AI avatars, marketing automation, business automation, AI services"
      />
      <Navigation />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 text-center">
          <Badge className="mb-4 text-lg px-6 py-2">AI Automation Services</Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
            Transform Your Business with AI Automation
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Comprehensive AI-powered solutions to automate your sales, marketing, customer service, and operations. Let artificial intelligence handle the repetitive tasks while you focus on growth.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button size="lg" asChild className="text-lg">
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">
                Schedule Free Consultation
              </a>
            </Button>
            <Button size="lg" variant="secondary" asChild className="text-lg">
              <a href="#services">
                Explore Services
              </a>
            </Button>
          </div>
        </section>

        {/* Stats Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <Card className="text-center border-2 border-primary/20">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-primary mb-2">24/7</div>
                <p className="text-muted-foreground">Always Available</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-primary/20">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-primary mb-2">10x</div>
                <p className="text-muted-foreground">Faster Response</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-primary/20">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-primary mb-2">70%</div>
                <p className="text-muted-foreground">Cost Reduction</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-primary/20">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-primary mb-2">5x</div>
                <p className="text-muted-foreground">Lead Generation</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our AI Automation Services</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive AI solutions tailored to your business needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow border-2 border-border hover:border-primary/40">
                  <CardHeader>
                    <div className="h-14 w-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl mb-2">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-primary">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Industries Section */}
        <section className="container mx-auto px-4 py-16 bg-muted/30 rounded-2xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Industries We Serve</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              AI automation solutions for businesses across all sectors
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {industries.map((industry, index) => (
              <Card key={index} className="text-center border-2 border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="pt-6">
                  <p className="font-semibold">{industry}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <Card className="text-center">
              <CardHeader>
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-primary">1</div>
                <CardTitle className="text-lg">Discovery Call</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">We learn about your business needs and automation goals</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-primary">2</div>
                <CardTitle className="text-lg">Custom Strategy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">We design an AI automation blueprint tailored to your workflows</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-primary">3</div>
                <CardTitle className="text-lg">Implementation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">Our team builds and deploys your AI automation systems</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-primary">4</div>
                <CardTitle className="text-lg">Training & Support</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">We train your team and provide ongoing optimization</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="container mx-auto px-4 py-16">
          <PricingSection />
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 py-16">
          <Card className="max-w-4xl mx-auto text-center border-4 border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
            <CardHeader>
              <CardTitle className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Automate Your Business?
              </CardTitle>
              <p className="text-xl text-muted-foreground">
                Join hundreds of businesses already using Neil AI to automate their operations and scale faster than ever before.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-4 justify-center flex-wrap">
                <Button size="lg" asChild className="text-lg">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">
                    Book Free Consultation
                  </a>
                </Button>
                <Button size="lg" variant="secondary" asChild className="text-lg">
                  <a href="#pricing">
                    View Pricing Plans
                  </a>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                🎓 Weekly training calls included • 🤝 White glove service available
              </p>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AIAutomation;